import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaUser, FaEdit, FaArrowLeft, FaPhone, FaEnvelope, FaMapMarkerAlt, FaIdCard, FaCalendar } from 'react-icons/fa';
import { sociosAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import './Socios.css';

const SocioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [socio, setSocio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSocio();
  }, [id]);

  const loadSocio = async () => {
    try {
      setLoading(true);
      const { data } = await sociosAPI.getById(id);
      setSocio(data.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do sócio');
      navigate('/socios');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    if (phone.length === 11) {
      return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="socios-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando dados do sócio...</p>
        </div>
      </div>
    );
  }

  if (!socio) {
    return (
      <div className="socios-container">
        <div className="empty-state">
          <p>Sócio não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="socios-container">
      <div className="page-header">
        <div>
          <h1><FaUser /> Detalhes do Sócio</h1>
          <p>Informações completas do cadastro</p>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/socios')} className="btn btn-secondary">
            <FaArrowLeft /> Voltar
          </button>
          {hasPermission('pode_alterar') && (
            <Link to={`/socios/${id}/editar`} className="btn btn-primary">
              <FaEdit /> Editar
            </Link>
          )}
        </div>
      </div>

      <div className="detail-container">
        {/* Header Card with Photo */}
        <div className="detail-header-card">
          <div className="socio-avatar-large">
            {socio.foto ? (
              <img src={socio.foto} alt={socio.nome} />
            ) : (
              <div className="avatar-placeholder">
                <FaUser />
              </div>
            )}
          </div>
          <div className="socio-header-info">
            <h2>{socio.nome}</h2>
            <div className="socio-badges">
              <span className="badge badge-large badge-primary">
                Título: {socio.titulo}
              </span>
              <span className={`badge badge-large ${socio.status === 'ativo' ? 'badge-success' : 'badge-danger'}`}>
                {socio.status}
              </span>
              <span className={`badge badge-large ${socio.quite === 'sim' ? 'badge-success' : 'badge-warning'}`}>
                {socio.quite === 'sim' ? 'Quite' : 'Não Quite'}
              </span>
            </div>
          </div>
        </div>

        {/* Detail Sections */}
        <div className="detail-grid">
          {/* Dados Cadastrais */}
          <div className="detail-card">
            <h3><FaIdCard /> Dados Cadastrais</h3>
            <div className="detail-row">
              <div className="detail-item">
                <label>Título</label>
                <span>{socio.titulo}</span>
              </div>
              <div className="detail-item">
                <label>Data de Emissão</label>
                <span>{formatDate(socio.emissao)}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item">
                <label>Status</label>
                <span className={socio.status === 'ativo' ? 'text-success' : 'text-danger'}>
                  {socio.status.toUpperCase()}
                </span>
              </div>
              <div className="detail-item">
                <label>Situação Financeira</label>
                <span className={socio.quite === 'sim' ? 'text-success' : 'text-warning'}>
                  {socio.quite === 'sim' ? 'Quite' : 'Não Quite'}
                </span>
              </div>
            </div>
          </div>

          {/* Dados Pessoais */}
          <div className="detail-card">
            <h3><FaUser /> Dados Pessoais</h3>
            <div className="detail-row">
              <div className="detail-item full-width">
                <label>Nome Completo</label>
                <span>{socio.nome}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item">
                <label>RG</label>
                <span>{socio.rg || '-'}</span>
              </div>
              <div className="detail-item">
                <label>CPF</label>
                <span>{formatCPF(socio.cpf)}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item">
                <label>Data de Nascimento</label>
                <span>{formatDate(socio.data_nascimento)}</span>
              </div>
              <div className="detail-item">
                <label>Idade</label>
                <span>{calculateAge(socio.data_nascimento)} anos</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item">
                <label>Sexo</label>
                <span>{socio.sexo ? socio.sexo.charAt(0).toUpperCase() + socio.sexo.slice(1) : '-'}</span>
              </div>
            </div>
          </div>

          {/* Documentação Eleitoral */}
          {(socio.titulo_eleitor || socio.zona || socio.secao) && (
            <div className="detail-card">
              <h3><FaIdCard /> Documentação Eleitoral</h3>
              <div className="detail-row">
                <div className="detail-item">
                  <label>Título de Eleitor</label>
                  <span>{socio.titulo_eleitor || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Zona</label>
                  <span>{socio.zona || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Seção</label>
                  <span>{socio.secao || '-'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Endereço */}
          <div className="detail-card">
            <h3><FaMapMarkerAlt /> Endereço</h3>
            <div className="detail-row">
              <div className="detail-item">
                <label>CEP</label>
                <span>{socio.cep || '-'}</span>
              </div>
              <div className="detail-item flex-2">
                <label>Endereço</label>
                <span>{socio.endereco || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Número</label>
                <span>{socio.numero || '-'}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item">
                <label>Complemento</label>
                <span>{socio.complemento || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Bairro</label>
                <span>{socio.bairro || '-'}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item flex-2">
                <label>Cidade</label>
                <span>{socio.cidade || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Estado</label>
                <span>{socio.estado || '-'}</span>
              </div>
            </div>
          </div>

          {/* Contato */}
          <div className="detail-card">
            <h3><FaPhone /> Contato</h3>
            <div className="detail-row">
              <div className="detail-item">
                <label>Telefone</label>
                <span>{formatPhone(socio.telefone)}</span>
              </div>
              <div className="detail-item">
                <label>Celular</label>
                <span>{formatPhone(socio.celular)}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item full-width">
                <label>E-mail</label>
                <span>{socio.email || '-'}</span>
              </div>
            </div>
          </div>

          {/* Informações do Sistema */}
          <div className="detail-card">
            <h3><FaCalendar /> Informações do Sistema</h3>
            <div className="detail-row">
              <div className="detail-item">
                <label>Data de Cadastro</label>
                <span>{formatDate(socio.created_at)}</span>
              </div>
              <div className="detail-item">
                <label>Última Atualização</label>
                <span>{formatDate(socio.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioDetail;